<script lang="ts" setup>
import { computed, onMounted, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import {
  ValidationResult,
  type ImportDomainsResponse as ImportDomainsResponseType
} from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import MigrationDomainList from './MigrationDomainList.vue'
import MigrationImportPanel from './MigrationImportPanel.vue'
import MigrationResultsDialog from './MigrationResultsDialog.vue'

const { t } = useI18n()
const notifyStore = useNotifyStore()

const data = reactive({
  loading: false,
  domains: [] as ValidationResult[],
  selectedDomains: [] as string[],
  showImportPanel: false,
  showResults: false,
  importResults: null as ImportDomainsResponseType | null
})

// Computed stats
const stats = computed(() => {
  const available = data.domains.filter((d) => d.can_import).length
  const imported = data.domains.filter((d) => d.exists_in_db).length
  return {
    total: data.domains.length,
    available,
    imported,
    selected: data.selectedDomains.length
  }
})

// Load available domains from INWX
const loadDomains = async () => {
  data.loading = true
  try {
    const res = await useAxios<null, ValidationResult[]>('GET', '/admin/import/inwx/domains', true)
    if (res && res.status === STATUS_CODES.OK) {
      const validate = ValidationResult.array().safeParse(res.data)
      if (validate.success) {
        data.domains = validate.data
      }
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'MigrationHome:loadDomains' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error)
    })
  } finally {
    data.loading = false
  }
}

// Handle domain selection changes
const handleSelectionChange = (domains: string[]) => {
  data.selectedDomains = domains
}

// Open import panel for selected domains
const handleImportSelected = () => {
  if (data.selectedDomains.length === 0) return
  data.showImportPanel = true
}

// Close import panel
const handleCloseImport = () => {
  data.showImportPanel = false
}

// Handle successful import
const handleImportComplete = (results: ImportDomainsResponseType) => {
  // Close drawer first
  data.showImportPanel = false

  // Clear selection
  data.selectedDomains = []

  // Refresh the domain list to update table state
  loadDomains()

  // Show notification with import summary
  const successCount = results.summary.successful
  const failedCount = results.summary.failed

  if (failedCount === 0) {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: 'Import erfolgreich',
      message: `${successCount} Domain${successCount !== 1 ? 's' : ''} erfolgreich importiert`
    })
  } else {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'warning',
      title: 'Import abgeschlossen',
      message: `${successCount} erfolgreich, ${failedCount} fehlgeschlagen`
    })
    // Show results dialog only if there were failures
    data.importResults = results
    data.showResults = true
  }
}

// Close results dialog
const handleCloseResults = () => {
  data.showResults = false
  data.importResults = null
}

// Import more domains (from results dialog)
const handleImportMore = () => {
  data.showResults = false
  data.importResults = null
}

onMounted(() => {
  loadDomains()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.migration.title')"
    :subtitle="$t('dashboard.admin.migration.subtitle')"
    icon="mdi-swap-horizontal-bold"
    icon-color="teal"
  >
    <!-- Header Actions -->
    <template #actions>
      <div class="header-actions">
        <button
          class="refresh-btn dashboard-action-btn"
          :disabled="data.loading"
          @click="loadDomains"
        >
          <v-icon icon="mdi-refresh" size="18" :class="{ 'spin': data.loading }" />
          <span>{{ $t('dashboard.admin.migration.actions.refresh') }}</span>
        </button>

        <button
          class="import-btn dashboard-action-btn dashboard-action-btn--primary"
          :disabled="data.selectedDomains.length === 0"
          @click="handleImportSelected"
        >
          <v-icon icon="mdi-import" size="18" />
          <span>{{ $t('dashboard.admin.migration.actions.importSelected') }}</span>
          <span v-if="data.selectedDomains.length > 0" class="count-badge">
            {{ data.selectedDomains.length }}
          </span>
        </button>
      </div>
    </template>

    <!-- Stats Pills -->
    <template #header-below>
      <div class="stats-container">
        <div class="stat-pill">
          <v-icon icon="mdi-domain" size="16" />
          <span class="stat-value">{{ stats.total }}</span>
          <span class="stat-label">Gesamt</span>
        </div>
        <div class="stat-pill stat-available">
          <v-icon icon="mdi-check-circle" size="16" />
          <span class="stat-value">{{ stats.available }}</span>
          <span class="stat-label">{{ $t('dashboard.admin.migration.stats.available') }}</span>
        </div>
        <div class="stat-pill stat-imported">
          <v-icon icon="mdi-database-check" size="16" />
          <span class="stat-value">{{ stats.imported }}</span>
          <span class="stat-label">{{ $t('dashboard.admin.migration.stats.imported') }}</span>
        </div>
        <div v-if="data.selectedDomains.length > 0" class="stat-pill stat-selected">
          <v-icon icon="mdi-checkbox-marked" size="16" />
          <span class="stat-value">{{ stats.selected }}</span>
          <span class="stat-label">{{ $t('dashboard.admin.migration.stats.selected') }}</span>
        </div>
      </div>
    </template>

    <!-- Domain List -->
    <MigrationDomainList
      :domains="data.domains"
      :loading="data.loading"
      :selected-domains="data.selectedDomains"
      @selection-change="handleSelectionChange"
      @import="handleImportSelected"
    />

    <!-- Import Panel (Slide-out) -->
    <MigrationImportPanel
      :visible="data.showImportPanel"
      :domains="data.domains.filter((d) => data.selectedDomains.includes(d.domain))"
      @close="handleCloseImport"
      @import-complete="handleImportComplete"
    />

    <!-- Results Dialog -->
    <MigrationResultsDialog
      :visible="data.showResults"
      :results="data.importResults"
      @close="handleCloseResults"
      @import-more="handleImportMore"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Header Actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
}

.refresh-btn,
.import-btn {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
  padding: var(--dashboard-btn-padding);
  border-radius: var(--dashboard-btn-radius);
  font-size: var(--dashboard-subtitle-size);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.refresh-btn {
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  color: rgba(200, 218, 255, 0.8);

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
    border-color: rgba(120, 163, 255, 0.3);
  }

  .spin {
    animation: spin 1s linear infinite;
  }
}

.import-btn {
  background: rgba(0, 150, 136, 0.1);
  border: 1px solid rgba(0, 150, 136, 0.25);
  color: #26a69a;

  &:hover:not(:disabled) {
    background: rgba(0, 150, 136, 0.15);
    border-color: rgba(0, 150, 136, 0.35);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: rgba(0, 150, 136, 0.3);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

/* Stats Pills */
.stats-container {
  display: flex;
  flex-wrap: wrap;
  gap: var(--dashboard-gap-sm);
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
  padding: 8px 12px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 24px;
  color: rgba(200, 218, 255, 0.7);

  .v-icon {
    color: rgba(200, 218, 255, 0.5);
  }

  .stat-value {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  .stat-label {
    font-size: var(--dashboard-stat-label-size);
    color: rgba(200, 218, 255, 0.5);
  }

  &.stat-available {
    background: rgba(76, 175, 80, 0.08);
    border-color: rgba(76, 175, 80, 0.2);

    .v-icon {
      color: #81c784;
    }

    .stat-value {
      color: #81c784;
    }
  }

  &.stat-imported {
    background: rgba(0, 150, 136, 0.08);
    border-color: rgba(0, 150, 136, 0.2);

    .v-icon {
      color: #26a69a;
    }

    .stat-value {
      color: #26a69a;
    }
  }

  &.stat-selected {
    background: rgba(33, 150, 243, 0.08);
    border-color: rgba(33, 150, 243, 0.2);

    .v-icon {
      color: #42a5f5;
    }

    .stat-value {
      color: #42a5f5;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .stat-pill {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);

    .stat-value {
      color: rgba(15, 23, 42, 0.9);
    }

    .stat-label {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
  }

  .refresh-btn,
  .import-btn {
    justify-content: center;
  }

  .stats-container {
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .refresh-btn,
  .import-btn {
    transition: none;

    &:hover {
      transform: none;
    }
  }

  .spin {
    animation: none;
  }
}
</style>
